/*
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Explore
 * (C) Copyright IBM Corp. 2018, 2021
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../../../lib/@waca/core-client/js/core-client/utils/UniqueId',
	'../../../../dashboard/util/DashboardSpecHelper',
	'../../../../app/nls/StringResources',
	'../../../../dashboard/glass/util/InstrumentationUtil'
], function(UniqueId, DashboardSpecHelper, StringResources, InstrumentationUtil) {

	const EXPLORE_PERSPECTIVE_ID = 'explore';

	/**
	 * This Class handles the flow to launch a dashboard visualization widget into
	 * Explore perspective, merging all the datasources, widgets, layout information
	 */
	class LaunchExploreHandler {
		constructor(options) {
			this.dashboard = options.dashboard;
			this.controller = options.controller; // InteractionController.internal

			this.specHelper = new DashboardSpecHelper(this.controller);
		}

		launchExplore(options) {
			const perspectiveID = UniqueId.get(EXPLORE_PERSPECTIVE_ID);
			const boardSpec = this._createEmptyBoardSpec();
			const addOnSpec = this._getAddOnSpec();

			this._track(addOnSpec, options);
			let isStylesStripped = false;

			return this.dashboard.getGlassSvc('.ConversionService').then(
				conversionSvc => conversionSvc.convert('DASHBOARD', 'EXPLORE', addOnSpec.spec)
			).then(convertedSpec => {
				const perspectiveOptions = this._getPerspectiveOptions(boardSpec, convertedSpec, perspectiveID, options);
				perspectiveOptions.content.openAppViewCallback = this._openAppViewCallback;
				perspectiveOptions.content.addOnSpec = this._getAddOnFragment(convertedSpec);
				if (perspectiveOptions.content.addOnSpec.hasOwnProperty('isStylesStripped')) {
					if (perspectiveOptions.content.addOnSpec.isStylesStripped === true) {
						isStylesStripped = true;
					}
					delete perspectiveOptions.content.addOnSpec.isStylesStripped;
				}
				for (const widgetId in perspectiveOptions.content.addOnSpec.widgets) {
					if (perspectiveOptions.content.addOnSpec.widgets.hasOwnProperty(widgetId)) {
						perspectiveOptions.content.addOnSpec.widgets[widgetId].titleMode = 'smartTitle';
					}
				}
				return this.dashboard.openAppView(EXPLORE_PERSPECTIVE_ID, perspectiveOptions);
			}).then((view) => {
				//Show toast message if any styles have been stripped off
				if (isStylesStripped) {
					this.dashboard.showToast(StringResources.get('explore_remove_widget_styles'), {
						type: 'info'
					});
				}
				if (view && view.currentContentView && view.currentContentView.openCardListPane) {
					view.currentContentView.openCardListPane();
				}
			});
		}

		_track(addOnSpec, { content = {} } = {}) {
			if (addOnSpec.spec) {
				const spec = JSON.parse(addOnSpec.spec);
				const payload = {
					objectType: EXPLORE_PERSPECTIVE_ID
				};

				let action;
				if (content.boardId) {
					payload.object = content.boardId;
					action = 'exportedToExistingExplore';
				} else {
					action = 'exportedToNewExplore';
				}

				if (spec.dataSources && spec.dataSources.sources) {
					payload.sources = spec.dataSources.sources;
				}

				if (spec.widgets) {
					payload.widgets = spec.widgets;
				}

				payload.type = 'Exported Object';

				// I don't like this. InstrumentationUtil needs glassContext
				const options = {};
				this.dashboard.prepareGlassOptions(options);
				const { glassContext } = options;

				InstrumentationUtil.track(glassContext, action, payload);
			}
		}

		_openAppViewCallback(addOnSpec, baseBoardView){
			if (addOnSpec) {
				baseBoardView.addFragment({
					model: addOnSpec
				}, {
					selectAfterAdd: true
				}, {
					skipUndoRedo: true
				});
			}
		}

		_getAddOnFragment(addOnSpec){
			if(!addOnSpec){
				return null;
			}
			let addOnSpecJSON = JSON.parse(addOnSpec);
			this.preprocess(addOnSpecJSON);
			return addOnSpecJSON;
		}

		_getPerspectiveOptions(boardSpec, convertedSpec, perspectiveID, { content } = {}) {
			const jsonObject = JSON.parse(convertedSpec);
			this.preprocess(jsonObject);

			//set content
			const oContent = content || {
				boardSpec: boardSpec,
				isAuthoringMode: true
			};
			//Append the addOn Spec, which is the spec collected from the selected source widgets
			oContent.addOnSpec = jsonObject;
			return  {
				id: oContent.boardId ||  perspectiveID,
				content: oContent
			};
		}

		/**
		 * Remove the root container JSON node and remove the visPlaceHolder
		 */
		preprocess(jsonObject){
			if(jsonObject && jsonObject.layout && jsonObject.layout.type === 'exploreContainer') {

				// Explore Card Model ID has to be unique
				jsonObject.layout.items.map(function(layoutItemEntry, index){
					layoutItemEntry.id = layoutItemEntry.id + '_' + index;
				});

				jsonObject.layout = jsonObject.layout.items;
			}
		}

		/**
		 * @private
		 * get add on spec from selected widgets
		 */
		_getAddOnSpec() {
			const selectedNodes = this.controller.getSelectedNodes();
			const { spec = null } = this.specHelper.getContentsToJSONSpec();

			return {
				spec,
				count: selectedNodes.length || 0
			};
		}

		/**
		 * !!! Warning
		 * !!! We are creating an explore spec from nothing
		 * !!! This should be something done in explore and not dashboard
		 * !!! Found the following RTC story that probably would handle this work
		 * RTC: 248470 - [Refactor] Launch exploration from dashboard is performed very intrusively
		 * @private
		 * Empty explore spec
		 */
		_createEmptyBoardSpec() {
			const LAYOUT_TYPE = 'exploreContainer';

			return {
				name: StringResources.get('launchNewExplore'),
				layout: {
					type: LAYOUT_TYPE,
					items: []
				},
				widgets: {},
				_meta: {
					bundleID: null
				}
			};
		}
	}

	return LaunchExploreHandler;
});
